import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

/**
 * Fournit l'état global d'appel VoIP (Twilio Voice, navigateur) :
 * initialisation du Device, démarrage/raccrochage, mute, minuterie, config.
 */
export function CallProvider({ children }) {
  const { user } = useAuth();
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const incomingRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle|connecting|ringing|in-call|ended|error
  const [meta, setMeta] = useState(null);        // {logId, contactName, spotName, toNumber, spotId}
  const [muted, setMuted] = useState(false);
  const [durationSec, setDurationSec] = useState(0);
  const [config, setConfig] = useState({ ready: false, phone_number: null });
  const [widgetOpen, setWidgetOpen] = useState(false);

  const refreshConfig = useCallback(() => {
    if (!localStorage.getItem("shariti_token")) return Promise.resolve();
    return api.get("/calls/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Interroge la config uniquement pour un utilisateur authentifié (rôle admin/cdp).
    // Se relance à la connexion (dépendance `user`) et évite tout 401 sur pages publiques.
    if (!user || !["admin", "cdp"].includes(user.role)) return;
    let alive = true;
    api.get("/calls/config").then((r) => { if (alive) setConfig(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const startTimer = () => {
    stopTimer();
    setDurationSec(0);
    timerRef.current = setInterval(() => setDurationSec((s) => s + 1), 1000);
  };
  useEffect(() => () => stopTimer(), []);

  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;
    const { data } = await api.get("/calls/token");
    const device = new Device(data.token, { codecPreferences: ["opus", "pcmu"], logLevel: "error" });
    device.on("error", (e) => console.error("[twilio] device error", e?.message || e));
    device.on("tokenWillExpire", async () => {
      try { const { data: d } = await api.get("/calls/token"); device.updateToken(d.token); } catch { /* noop */ }
    });
    // Appel ENTRANT : le numéro Shariti fait sonner ce navigateur (standard).
    device.on("incoming", (call) => {
      // Déjà en communication / une autre sonnerie active -> on refuse (un autre poste prendra).
      if (callRef.current || incomingRef.current) { try { call.reject(); } catch { /* noop */ } return; }
      incomingRef.current = call;
      const cp = call.customParameters;
      const from = (cp && cp.get("fromNumber")) || call.parameters?.From || "";
      const logId = cp && cp.get("logId");
      setMeta({ direction: "inbound", fromNumber: from, toNumber: from, contactName: null, spotName: null, logId });
      setStatus("incoming");
      setWidgetOpen(true);
      call.on("cancel", () => {
        // Décroché par un autre poste, ou l'appelant a raccroché avant décrochage.
        if (incomingRef.current === call) {
          incomingRef.current = null;
          setStatus("idle"); setWidgetOpen(false); setMeta(null);
        }
      });
    });
    await device.register();
    deviceRef.current = device;
    return device;
  }, []);

  const attachCall = useCallback((call) => {
    callRef.current = call;
    call.on("ringing", () => setStatus("ringing"));
    call.on("accept", () => { setStatus("in-call"); startTimer(); });
    call.on("disconnect", () => { setStatus("ended"); stopTimer(); callRef.current = null; setMuted(false); });
    call.on("cancel", () => { setStatus("ended"); stopTimer(); callRef.current = null; });
    call.on("reject", () => { setStatus("ended"); stopTimer(); callRef.current = null; });
    call.on("error", (e) => { console.error("[twilio] call error", e?.message || e); setStatus("error"); stopTimer(); });
  }, []);

  // --- Appels entrants : accepter / refuser depuis le widget ---
  const acceptIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (!call) return;
    attachCall(call);
    try { call.accept(); } catch { /* noop */ }
    incomingRef.current = null;
    setMuted(false);
  }, [attachCall]);

  const rejectIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (call) { try { call.reject(); } catch { /* noop */ } }
    incomingRef.current = null;
    setStatus("idle"); setWidgetOpen(false); setMeta(null);
  }, []);

  // Enregistre proactivement le Device (pour RECEVOIR les appels entrants) et
  // maintient un battement de présence tant qu'un agent (admin/cdp) est connecté.
  useEffect(() => {
    if (!user || !["admin", "cdp"].includes(user.role)) return;
    if (!config.ready) return;
    let alive = true;
    let hb = null;
    (async () => {
      try { await ensureDevice(); } catch { return; }
      if (!alive) return;
      const beat = () => { api.post("/calls/presence").catch(() => {}); };
      beat();
      hb = setInterval(beat, 15000);
    })();
    return () => { alive = false; if (hb) clearInterval(hb); };
  }, [user, config.ready, ensureDevice]);

  const startCall = useCallback(async ({ spotId, toNumber, contactName, spotName }) => {
    if (!config.ready) {
      toast.info("Téléphonie en attente : le numéro Twilio doit encore être activé.");
      return;
    }
    if (!toNumber) { toast.error("Aucun numéro de téléphone disponible pour ce contact."); return; }
    if (status === "connecting" || status === "ringing" || status === "in-call") {
      toast.info("Un appel est déjà en cours."); return;
    }
    try {
      setStatus("connecting");
      setWidgetOpen(true);
      setMuted(false);
      const { data: log } = await api.post("/calls", {
        spot_id: spotId, to_number: toNumber, contact_name: contactName, spot_name: spotName,
      });
      setMeta({ logId: log.id, contactName, spotName, toNumber: log.to_number, spotId });
      const device = await ensureDevice();
      const call = await device.connect({ params: { To: log.to_number, LogId: log.id } });
      attachCall(call);
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Impossible de démarrer l'appel.");
      setStatus("error");
    }
  }, [config.ready, status, ensureDevice, attachCall]);

  const hangup = useCallback(() => {
    try { callRef.current?.disconnect(); } catch { /* noop */ }
    try { deviceRef.current?.disconnectAll(); } catch { /* noop */ }
    setStatus("ended");
    stopTimer();
  }, []);

  const toggleMute = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    const next = !muted;
    try { c.mute(next); setMuted(next); } catch { /* noop */ }
  }, [muted]);

  // Envoie une tonalité DTMF sur l'appel en cours (serveurs vocaux : « tapez 1 »,
  // « tapez 2 pour la direction »…). Accepte 0-9, * et #.
  const sendDigit = useCallback((digit) => {
    const c = callRef.current;
    if (!c) return;
    try { c.sendDigits(String(digit)); } catch { /* noop */ }
  }, []);

  const closeWidget = useCallback(() => {
    setWidgetOpen(false);
    setStatus("idle");
    setMeta(null);
    setDurationSec(0);
    setMuted(false);
  }, []);

  const value = {
    status, meta, muted, durationSec, config, widgetOpen,
    startCall, hangup, toggleMute, sendDigit, closeWidget, refreshConfig,
    acceptIncoming, rejectIncoming,
  };
  return <CallCtx.Provider value={value}>{children}</CallCtx.Provider>;
}
